/* ============================================================
   SCENTIMENTAL — PRODUCT PAGE BEHAVIOUR

   Owns: gallery, variant + pack selection, quantity, running subtotal,
   per-unit pricing, free-shipping goal, offer codes, delivery estimate,
   buy-it-together basket, accordions, sticky bar, add-to-cart feedback.

   Does NOT own: the cart request itself or the cart drawer — cart.js
   owns those and binds to .scentimental-add-to-cart-form and
   [data-buy-now]. This file only keeps form state correct before
   cart.js reads it, and reacts to the result.
============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-pdp]');
  if (!root) return;

  /* Entrance animations are opt-IN, switched on by this class before any
     other code runs. Every "start hidden" rule is scoped to .sc-anim, so
     if this file fails to load — or throws on line two — the page renders
     fully visible instead of leaving the buy rail at opacity 0. */
  root.classList.add('sc-anim');

  var form         = root.querySelector('[data-buy-form]');
  var variantInput = root.querySelector('[data-variant-input]');
  var qtyInput     = root.querySelector('[data-qty-input]');
  var priceNow     = root.querySelector('[data-price-now]');
  var priceWas     = root.querySelector('[data-price-was]');
  var priceSave    = root.querySelector('[data-price-save]');
  // two places show the live unit price: beside the tax note, and as the
  // value on the Size label ("Size — Rs 1.67/ml")
  var unitPriceEls = root.querySelectorAll('[data-unit-price]');
  var subtotals    = root.querySelectorAll('[data-subtotal]');
  var stickyBar    = root.querySelector('[data-sticky-bar]');
  var stickyVar    = root.querySelector('[data-sticky-variant]');
  var sizeCurrent  = root.querySelector('[data-size-current]');
  var toast        = root.querySelector('[data-toast]');
  var toastText    = root.querySelector('[data-toast-text]');
  var buyNowBtns   = root.querySelectorAll('[data-buy-now]');

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------
     Money.

     Everything is computed from INTEGER CENTS supplied by Liquid
     and printed through the shop's own money_format, so JS output
     always matches server-rendered prices.

     Do not go back to parsing rendered money strings: "Rs. 699.00"
     carries a period that is not a decimal point, which made the
     old parser read it as 0.699 and print "Rs0.70 699.00".
  ---------------------------------------------------------- */
  var CFG = window.SCENT_PDP || {};
  var MONEY_FORMAT = CFG.moneyFormat || 'Rs. {{amount}}';
  var LOCALE = CFG.locale || 'en-IN';

  function formatMoney(cents) {
    var token = MONEY_FORMAT.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!token) return MONEY_FORMAT;

    var name = token[1];
    var decimals = name.indexOf('no_decimals') !== -1 ? 0 : 2;
    // *_with_comma_separator means European style: 1.234,56
    var european = name.indexOf('comma_separator') !== -1;
    var amount = (Number(cents) || 0) / 100;

    var printed = amount.toLocaleString(european ? 'de-DE' : LOCALE, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });

    return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, printed);
  }

  // currency symbol only, for the compact "₹1.67/ml" form where the
  // full money_format would be too heavy to sit under a chip
  function moneySymbol() {
    return MONEY_FORMAT.replace(/\{\{\s*\w+\s*\}\}/, '').trim() || '';
  }

  /* ----------------------------------------------------------
     State
  ---------------------------------------------------------- */
  var unitCents = priceNow ? Number(priceNow.getAttribute('data-cents')) || 0 : 0;

  function currentQty() {
    var n = parseInt(qtyInput && qtyInput.value, 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function renderSubtotal() {
    var text = formatMoney(unitCents * currentQty());
    for (var i = 0; i < subtotals.length; i++) subtotals[i].textContent = text;
    renderGoal();
  }

  /* ----------------------------------------------------------
     Per-unit pricing.

     The volume is read from the variant's own title ("300 ML"), which
     is data the merchant already maintains — no new metafield to keep
     in sync, and nothing invented when a title carries no size.

     Showing ₹/ml turns the upsell into arithmetic the buyer does
     themselves, which is worth more than being told.
  ---------------------------------------------------------- */
  var SIZE_RE = /(\d+(?:[.,]\d+)?)\s*(ml|l|g|gm|gms|kg|oz)\b/i;

  function parseSize(text) {
    if (!text) return null;
    var m = String(text).match(SIZE_RE);
    if (!m) return null;
    var value = parseFloat(m[1].replace(',', '.'));
    if (!value || value <= 0) return null;
    var unit = m[2].toLowerCase();
    // normalise so litres and kilos compare against their base unit
    if (unit === 'l') { value *= 1000; unit = 'ml'; }
    if (unit === 'kg') { value *= 1000; unit = 'g'; }
    if (unit === 'gm' || unit === 'gms') unit = 'g';
    return { value: value, unit: unit };
  }

  // fall back to the product title so single-variant products
  // ("First Crush 100 ml") still get a unit price
  var productTitle = buyNowBtns.length ? buyNowBtns[0].getAttribute('data-product-title') : '';

  function unitPriceText(cents, title) {
    var size = parseSize(title) || parseSize(productTitle);
    if (!size || !cents) return '';
    var per = cents / 100 / size.value;
    // sub-rupee unit prices need two decimals to say anything at all
    var digits = per < 1 ? 2 : per < 10 ? 2 : 0;
    return moneySymbol() + per.toFixed(digits) + '/' + size.unit;
  }

  function renderUnitPrice(cents, title) {
    var text = unitPriceText(cents, title);
    for (var i = 0; i < unitPriceEls.length; i++) {
      unitPriceEls[i].textContent = text;
      unitPriceEls[i].hidden = !text;
    }
  }

  /* Stamp every size chip with its own unit price, and flag the one
     that costs least per ml. The flag is computed, never authored, so
     it can never contradict the prices printed beside it. */
  (function paintSizeChips() {
    var chips = root.querySelectorAll('.scentimental-variant-btn');
    if (!chips.length) return;

    var best = null;
    var rates = [];

    for (var i = 0; i < chips.length; i++) {
      var cents = Number(chips[i].getAttribute('data-variant-cents')) || 0;
      var size = parseSize(chips[i].getAttribute('data-variant-title'));
      var slot = chips[i].querySelector('[data-size-unit]');
      var rate = null;

      if (size && cents) {
        rate = cents / size.value;
        if (slot) slot.textContent = unitPriceText(cents, chips[i].getAttribute('data-variant-title'));
      }

      rates.push(rate);
      if (rate !== null && chips[i].getAttribute('data-variant-available') === 'true') {
        if (best === null || rate < rates[best]) best = i;
      }
    }

    // only worth flagging when there is a genuine spread to point at
    var hasSpread = false;
    for (var j = 0; j < rates.length; j++) {
      if (rates[j] !== null && best !== null && j !== best && rates[j] > rates[best] * 1.02) hasSpread = true;
    }

    if (best !== null && hasSpread) {
      var flag = chips[best].querySelector('[data-size-best]');
      if (flag) flag.hidden = false;
    }
  })();

  /* ----------------------------------------------------------
     Free-shipping goal
  ---------------------------------------------------------- */
  var goal      = root.querySelector('[data-goal]');
  var goalText  = root.querySelector('[data-goal-text]');
  var goalFill  = root.querySelector('[data-goal-fill]');
  var goalCents = Number(root.getAttribute('data-free-ship')) || 0;

  function renderGoal() {
    if (!goal || !goalCents) return;
    var total = unitCents * currentQty();
    var pct = Math.min(100, (total / goalCents) * 100);

    goal.hidden = false;
    if (goalFill) goalFill.style.width = pct + '%';

    if (total >= goalCents) {
      goal.classList.add('is-met');
      if (goalText) goalText.innerHTML = '<strong>Free shipping unlocked.</strong>';
    } else {
      goal.classList.remove('is-met');
      if (goalText) {
        goalText.innerHTML = 'You are <strong>' + formatMoney(goalCents - total) +
          '</strong> away from free shipping.';
      }
    }
  }

  /* ----------------------------------------------------------
     Variant selection
     Note: the price element and the gallery live OUTSIDE the
     form, so every lookup here is scoped to `root`, not `form`.
  ---------------------------------------------------------- */
  function selectVariant(btn) {
    if (!btn || btn.hasAttribute('disabled')) return;

    var id           = btn.getAttribute('data-variant-id');
    var price        = btn.getAttribute('data-variant-price');
    var cents        = Number(btn.getAttribute('data-variant-cents')) || 0;
    var compare      = btn.getAttribute('data-variant-compare');
    var compareCents = Number(btn.getAttribute('data-variant-compare-cents')) || 0;
    var title        = btn.getAttribute('data-variant-title');

    if (variantInput && id) variantInput.value = id;

    if (cents) {
      var changed = cents !== unitCents;
      unitCents = cents;
      if (priceNow) {
        priceNow.textContent = price || formatMoney(cents);
        priceNow.setAttribute('data-cents', cents);
        if (changed) {
          // restart the animation by forcing a reflow between toggles
          priceNow.classList.remove('is-changed');
          void priceNow.offsetWidth;
          priceNow.classList.add('is-changed');
        }
      }
    }

    renderUnitPrice(cents, title);

    // compare-at + saving travel together; hide both when not on sale
    if (priceWas) {
      if (compareCents > cents) {
        priceWas.textContent = compare || formatMoney(compareCents);
        priceWas.hidden = false;
        if (priceSave) {
          priceSave.textContent = formatMoney(compareCents - cents) + ' off';
          priceSave.hidden = false;
        }
      } else {
        priceWas.hidden = true;
        if (priceSave) priceSave.hidden = true;
      }
    }

    // keep every Buy Now trigger pointed at the selected variant
    for (var b = 0; b < buyNowBtns.length; b++) {
      if (!id) break;
      buyNowBtns[b].setAttribute('data-product-id', id);
      if (price) buyNowBtns[b].setAttribute('data-product-price', price);
      if (title) buyNowBtns[b].setAttribute('data-product-variant', title);
      var img = btn.getAttribute('data-variant-image');
      if (img) buyNowBtns[b].setAttribute('data-product-image', img);
    }

    if (stickyVar && title) stickyVar.textContent = ' · ' + title;
    if (sizeCurrent && title) sizeCurrent.textContent = title;

    // the bundle's anchor item is this product — keep it in step
    if (bundleAnchor && id) {
      bundleAnchor.setAttribute('data-variant', id);
      var anchorItem = bundleAnchor.closest('[data-bundle-item]');
      if (anchorItem) {
        anchorItem.setAttribute('data-cents', cents);
        // the compare-at has to travel with the price. Left stale, the bundle
        // keeps striking through the previous variant's total and shows a
        // saving the buyer is not actually getting.
        anchorItem.setAttribute('data-compare', compareCents > cents ? compareCents : cents);
        var anchorPrice = anchorItem.querySelector('.sc-bundle__price');
        if (anchorPrice) anchorPrice.textContent = price || formatMoney(cents);
      }
      renderBundle();
    }

    // active state
    var all = root.querySelectorAll('.scentimental-variant-btn');
    for (var i = 0; i < all.length; i++) {
      var isMe = all[i] === btn;
      all[i].classList.toggle('is-active', isMe);
      all[i].setAttribute('aria-checked', isMe ? 'true' : 'false');
    }

    // reflect the variant in the URL so the page is shareable and
    // survives a refresh — without adding a history entry per tap
    if (id && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', id);
      window.history.replaceState({}, '', url.toString());
    }

    renderSubtotal();
  }

  /* ----------------------------------------------------------
     Buy it together

     Builds items[n][id] / items[n][quantity] inputs so cart.js can
     POST the whole basket with its existing handler. The inputs are
     rebuilt on every change AND again on submit in the capture phase,
     which runs before cart.js reads FormData off the form.
  ---------------------------------------------------------- */
  var bundleForm   = root.querySelector('[data-bundle-form]');
  var bundleChecks = root.querySelectorAll('[data-bundle-check]');
  var bundleAnchor = root.querySelector('.sc-bundle__item.is-anchor [data-bundle-check]');
  var bundleTotal  = root.querySelector('[data-bundle-total]');
  var bundleCount  = root.querySelector('[data-bundle-count]');
  var bundleWas    = root.querySelector('[data-bundle-was]');

  function renderBundle() {
    if (!bundleForm) return;
    var total = 0;
    var wasTotal = 0;
    var count = 0;

    for (var i = 0; i < bundleChecks.length; i++) {
      if (!bundleChecks[i].checked) continue;
      var item = bundleChecks[i].closest('[data-bundle-item]');
      if (!item) continue;
      var cents = Number(item.getAttribute('data-cents')) || 0;
      total += cents;
      // falls back to the selling price, so an item with no compare-at
      // contributes nothing to a fake saving
      wasTotal += Number(item.getAttribute('data-compare')) || cents;
      count++;
    }

    if (bundleTotal) bundleTotal.textContent = formatMoney(total);
    if (bundleCount) bundleCount.textContent = count;

    /* The struck-through total is the sum of real compare-at prices.
       When no item is actually discounted the two sums are equal and
       the strikethrough hides itself rather than inventing a saving. */
    if (bundleWas) {
      if (wasTotal > total) {
        bundleWas.textContent = formatMoney(wasTotal);
        bundleWas.hidden = false;
      } else {
        bundleWas.hidden = true;
      }
    }

    var cta = bundleForm.querySelector('button[type="submit"]');
    if (cta) cta.disabled = count === 0;
  }

  function buildBundleInputs() {
    if (!bundleForm) return;

    var old = bundleForm.querySelectorAll('[data-bundle-input]');
    for (var o = 0; o < old.length; o++) old[o].remove();

    var n = 0;
    for (var i = 0; i < bundleChecks.length; i++) {
      if (!bundleChecks[i].checked) continue;
      var id = bundleChecks[i].getAttribute('data-variant');
      if (!id) continue;

      var idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'items[' + n + '][id]';
      idInput.value = id;
      idInput.setAttribute('data-bundle-input', '');

      var qtyIn = document.createElement('input');
      qtyIn.type = 'hidden';
      qtyIn.name = 'items[' + n + '][quantity]';
      qtyIn.value = '1';
      qtyIn.setAttribute('data-bundle-input', '');

      bundleForm.appendChild(idInput);
      bundleForm.appendChild(qtyIn);
      n++;
    }
  }

  if (bundleForm) {
    for (var bc = 0; bc < bundleChecks.length; bc++) {
      bundleChecks[bc].addEventListener('change', function () {
        renderBundle();
        buildBundleInputs();
      });
    }
    renderBundle();
    buildBundleInputs();

    // capture phase on the document beats the form's own bubble-phase
    // listener in cart.js, so the inputs are always in place first
    document.addEventListener('submit', function (event) {
      if (event.target === bundleForm) buildBundleInputs();
    }, true);
  }

  /* ----------------------------------------------------------
     Wiring
  ---------------------------------------------------------- */
  root.addEventListener('click', function (event) {
    // variant chips
    var variantBtn = event.target.closest('.scentimental-variant-btn');
    if (variantBtn) {
      event.preventDefault();
      selectVariant(variantBtn);
      return;
    }

    // pack options — a different product, so navigate to it
    var pack = event.target.closest('[data-pack]');
    if (pack) {
      var url = pack.getAttribute('data-pack-url');
      if (url) {
        event.preventDefault();
        window.location.href = url;
      }
      return;
    }

    // quantity
    var down = event.target.closest('[data-qty-down]');
    var up   = event.target.closest('[data-qty-up]');
    if (down || up) {
      event.preventDefault();
      if (!qtyInput) return;
      var next = currentQty() + (up ? 1 : -1);
      qtyInput.value = next < 1 ? 1 : next;
      renderSubtotal();
      return;
    }

    // accordions
    var trigger = event.target.closest('[data-acc-trigger]');
    if (trigger) {
      event.preventDefault();
      var item = trigger.closest('[data-acc]');
      if (!item) return;
      var open = item.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    // offer codes — claiming beats reading
    var codeBtn = event.target.closest('[data-copy-code]');
    if (codeBtn) {
      event.preventDefault();
      copyCode(codeBtn);
      return;
    }

    // in-page anchors (the rating link)
    var jump = event.target.closest('[data-scroll-to]');
    if (jump) {
      var target = document.querySelector(jump.getAttribute('href'));
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      }
      return;
    }
  });

  if (qtyInput) {
    qtyInput.addEventListener('change', function () {
      if (currentQty() !== parseInt(qtyInput.value, 10)) qtyInput.value = currentQty();
      renderSubtotal();
    });
  }

  /* ----------------------------------------------------------
     Offer codes
  ---------------------------------------------------------- */
  function copyCode(btn) {
    var code = btn.getAttribute('data-copy-code');
    var label = btn.querySelector('[data-copy-label]');
    if (!code || !label) return;

    var original = btn.getAttribute('data-original') || label.textContent;
    btn.setAttribute('data-original', original);

    function done() {
      label.textContent = 'Copied';
      btn.classList.add('is-copied');
      clearTimeout(btn._t);
      btn._t = setTimeout(function () {
        label.textContent = original;
        btn.classList.remove('is-copied');
      }, 1800);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, fallback);
    } else {
      fallback();
    }

    // execCommand is deprecated but is the only path on http:// origins
    // and inside some in-app browsers, which is exactly where this
    // store's traffic lands from Instagram
    function fallback() {
      var ta = document.createElement('textarea');
      ta.value = code;
      ta.setAttribute('readonly', '');
      ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { /* clipboard unavailable */ }
      document.body.removeChild(ta);
    }
  }

  /* ----------------------------------------------------------
     Delivery estimate

     The window comes from merchant settings, not from a carrier API.
     It is phrased as an estimate for exactly that reason.
  ---------------------------------------------------------- */
  (function delivery() {
    var box = root.querySelector('[data-deliver]');
    if (!box) return;

    var input  = box.querySelector('[data-pin-input]');
    var button = box.querySelector('[data-pin-check]');
    var result = box.querySelector('[data-pin-result]');
    if (!input || !button || !result) return;

    var minDays = Number(root.getAttribute('data-lead-min')) || 3;
    var maxDays = Number(root.getAttribute('data-lead-max')) || 7;
    if (maxDays < minDays) maxDays = minDays;

    function addDays(days) {
      var d = new Date();
      d.setDate(d.getDate() + days);
      return d.toLocaleDateString(LOCALE, { weekday: 'short', day: 'numeric', month: 'short' });
    }

    function check() {
      var pin = (input.value || '').replace(/\D/g, '');

      // Indian PINs are six digits and never start with zero
      if (!/^[1-9]\d{5}$/.test(pin)) {
        result.hidden = false;
        result.classList.add('is-error');
        result.textContent = 'Enter a valid 6-digit pincode.';
        return;
      }

      result.hidden = false;
      result.classList.remove('is-error');
      result.innerHTML = 'Estimated delivery to <strong>' + pin + '</strong> between <strong>' +
        addDays(minDays) + '</strong> and <strong>' + addDays(maxDays) + '</strong>.';

      try { localStorage.setItem('sc-pin', pin); } catch (e) { /* private mode */ }
    }

    button.addEventListener('click', check);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); check(); }
    });

    // strip anything that is not a digit as it is typed
    input.addEventListener('input', function () {
      var cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (cleaned !== input.value) input.value = cleaned;
      result.hidden = true;
    });

    // a returning visitor should not retype a pincode they already gave
    try {
      var saved = localStorage.getItem('sc-pin');
      if (saved) { input.value = saved; check(); }
    } catch (e) { /* private mode */ }
  })();

  /* ----------------------------------------------------------
     Before / after comparison

     The range input is the actual control — it sits invisible across
     the whole frame, so pointer users drag anywhere and keyboard users
     get a real labelled slider with arrow keys, for free. All the
     visual layers read one custom property.
  ---------------------------------------------------------- */
  (function compare() {
    var box = root.querySelector('[data-compare]');
    if (!box) return;

    var range = box.querySelector('[data-compare-range]');
    if (!range) return;

    function paint() {
      box.style.setProperty('--pos', range.value + '%');
    }

    range.addEventListener('input', paint);
    paint();

    /* A slider nobody notices is a slider nobody drags. Once the block
       scrolls into view the divider sweeps off-centre and settles back,
       which advertises that the thing is draggable without a tooltip. */
    if (!REDUCED && 'IntersectionObserver' in window) {
      var teased = false;
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting || teased) return;
        teased = true;
        io.disconnect();

        var start = performance.now();
        var DUR = 1400;
        var cancelled = false;

        function tick(now) {
          if (cancelled) return;
          var t = Math.min((now - start) / DUR, 1);
          // one soft cycle out to 68% and back to centre
          var eased = Math.sin(t * Math.PI);
          box.style.setProperty('--pos', (50 + eased * 18) + '%');
          if (t < 1) {
            requestAnimationFrame(tick);
          } else {
            range.value = 50;
            paint();
          }
        }

        // a real drag mid-tease wins outright: stop the animation and
        // hand the divider straight back to the pointer
        range.addEventListener('pointerdown', function () {
          cancelled = true;
        }, { once: true });

        requestAnimationFrame(tick);
      }, { threshold: 0.4 });

      io.observe(box);
    }
  })();

  /* ----------------------------------------------------------
     Gallery progress
  ---------------------------------------------------------- */
  var gallery = root.querySelector('[data-gallery]');
  var rail = root.querySelector('[data-gallery-rail]');

  if (rail && gallery) (function galleryModule() {
    var progress = root.querySelector('[data-gallery-progress]');
    var segs     = progress ? progress.querySelectorAll('.sc-gallery__seg') : [];
    var fills    = progress ? progress.querySelectorAll('.sc-gallery__seg-fill') : [];
    var thumbs   = root.querySelectorAll('[data-gallery-thumb]');
    var indexEl  = root.querySelector('[data-gallery-index]');
    var slides   = rail.querySelectorAll('.sc-gallery__slide');
    var count    = slides.length;
    if (count < 2) return;

    var DWELL = 5000;          // time a slide holds before advancing
    var RESUME_AFTER = 5000;   // idle time before autoplay resumes post-interaction
    var current = 0;
    var timer = null;
    var resumeTimer = null;
    var paused = false;
    var inView = true;

    function paintState(i) {
      for (var s = 0; s < segs.length; s++) {
        segs[s].classList.toggle('is-active', s === i);
        segs[s].classList.toggle('is-done', s < i);
      }
      for (var t = 0; t < thumbs.length; t++) {
        thumbs[t].classList.toggle('is-active', Number(thumbs[t].getAttribute('data-gallery-thumb')) === i);
      }
      if (indexEl) indexEl.textContent = i + 1;
    }

    // Drive the active segment's fill across the dwell window so the
    // pacing is legible instead of the slide simply jumping.
    function runFill(i) {
      for (var f = 0; f < fills.length; f++) {
        fills[f].style.transition = 'none';
        fills[f].style.width = f < i ? '100%' : '0';
      }
      var fill = fills[i];
      if (!fill || paused || REDUCED) return;
      requestAnimationFrame(function () {
        fill.style.transition = 'width ' + DWELL + 'ms linear';
        fill.style.width = '100%';
      });
    }

    function goTo(i, smooth) {
      current = (i + count) % count;
      rail.scrollTo({ left: current * rail.clientWidth, behavior: smooth === false ? 'auto' : 'smooth' });
      paintState(current);
    }

    function stop() {
      clearTimeout(timer);
      timer = null;
      for (var f = 0; f < fills.length; f++) {
        // freeze the bar where it is rather than snapping it back
        var w = getComputedStyle(fills[f]).width;
        fills[f].style.transition = 'none';
        fills[f].style.width = w;
      }
    }

    function start() {
      if (REDUCED || paused || !inView) return;
      stop();
      runFill(current);
      timer = setTimeout(function () {
        goTo(current + 1);
        start();
      }, DWELL);
    }

    /* user interaction pauses autoplay, then it resumes once idle */
    function interrupt() {
      paused = true;
      stop();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        paused = false;
        start();
      }, RESUME_AFTER);
    }

    var ticking = false;
    rail.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var i = Math.round(rail.scrollLeft / rail.clientWidth);
        if (i !== current) { current = i; paintState(i); }
        ticking = false;
      });
    }, { passive: true });

    ['pointerdown', 'touchstart'].forEach(function (evt) {
      rail.addEventListener(evt, interrupt, { passive: true });
    });

    /* A wheel event over the rail is usually the page scrolling past, not
       someone driving the carousel. Treating every one of them as an
       interaction pushed the gap between slides from the 5s dwell to
       5s + the 5s resume delay, so the gallery looked like it advanced
       roughly every ten seconds. Only horizontal intent counts. */
    rail.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) interrupt();
    }, { passive: true });

    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { interrupt(); goTo(current - 1); });
    if (next) next.addEventListener('click', function () { interrupt(); goTo(current + 1); });

    for (var t = 0; t < thumbs.length; t++) {
      thumbs[t].addEventListener('click', function () {
        interrupt();
        goTo(Number(this.getAttribute('data-gallery-thumb')));
      });
    }

    gallery.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { interrupt(); goTo(current + 1); }
      if (e.key === 'ArrowLeft')  { interrupt(); goTo(current - 1); }
    });

    // hovering should not fight the reader
    gallery.addEventListener('mouseenter', function () { paused = true; stop(); });
    gallery.addEventListener('mouseleave', function () { paused = false; start(); });

    // never animate a gallery nobody is looking at
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        inView = entries[0].isIntersecting;
        if (inView) start(); else stop();
      }, { threshold: 0.25 }).observe(gallery);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stop(); else start();
    });

    paintState(0);
    start();
  })();

  /* ----------------------------------------------------------
     Sticky bar — reveal once the main buy button is off screen
  ---------------------------------------------------------- */
  if (stickyBar && form) {
    var anchor = form.querySelector('button[type="submit"]');
    if (anchor && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        var visible = entries[0].isIntersecting;
        stickyBar.classList.toggle('is-visible', !visible);
        stickyBar.setAttribute('aria-hidden', visible ? 'true' : 'false');
        // theme.liquid's .to-top button is fixed at the same corner with a
        // higher z-index, so it has to move out of the bar's way
        document.body.classList.toggle('sc-sticky-open', !visible);
      }, { rootMargin: '0px 0px -40px 0px' });
      observer.observe(anchor);
    }
  }

  /* ----------------------------------------------------------
     Fly to cart

     The item physically travels to the cart icon. This is the one
     moment on the page worth animating for its own sake: it makes the
     add read as completed rather than merely acknowledged.
  ---------------------------------------------------------- */
  function flyToCart(sourceImg) {
    if (REDUCED || !sourceImg) return;

    var target = document.querySelector('[data-drawer-toggle="scentimental-cart-drawer"]') ||
                 document.querySelector('.header-cart, .cart-icon, [href$="/cart"]');

    var from = sourceImg.getBoundingClientRect();
    if (!from.width) return;

    var to = target
      ? target.getBoundingClientRect()
      : { left: window.innerWidth - 60, top: 20, width: 30, height: 30 };

    var ghost = document.createElement('div');
    ghost.className = 'sc-fly';
    ghost.style.left = from.left + from.width / 2 - 37 + 'px';
    ghost.style.top = from.top + from.height / 2 - 37 + 'px';

    var img = document.createElement('img');
    img.src = sourceImg.currentSrc || sourceImg.src;
    img.alt = '';
    ghost.appendChild(img);
    document.body.appendChild(ghost);

    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);

    requestAnimationFrame(function () {
      ghost.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(0.18) rotate(-8deg)';
      ghost.style.opacity = '0.15';
    });

    setTimeout(function () { ghost.remove(); }, 800);
  }

  /* ----------------------------------------------------------
     Add-to-cart feedback

     cart.js preventDefaults and owns the request; this reacts to the
     same gesture. The button confirms on itself, the item flies to the
     cart, and the toast is the backstop for anyone who looked away.
  ---------------------------------------------------------- */
  var toastTimer = null;

  function showToast(message) {
    if (!toast) return;
    if (toastText && message) toastText.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, 1800);
  }

  function bindAddFeedback(theForm, message, sourceImg) {
    if (!theForm) return;

    theForm.addEventListener('submit', function () {
      showToast(message);
      flyToCart(sourceImg());

      var submitBtn = theForm.querySelector('button[type="submit"]');
      var triggers = theForm === form
        ? root.querySelectorAll('.sc-btn--cta, .sc-btn--cream, .sc-sticky__cta')
        : theForm.querySelectorAll('button[type="submit"]');

      for (var i = 0; i < triggers.length; i++) triggers[i].classList.add('is-busy');

      var clear = function () {
        for (var j = 0; j < triggers.length; j++) {
          triggers[j].classList.remove('is-busy');
          triggers[j].classList.add('is-done');
        }
        setTimeout(function () {
          for (var k = 0; k < triggers.length; k++) triggers[k].classList.remove('is-done');
        }, 1500);
      };

      // cart.js re-enables its submit button in a finally block, so we
      // clear on that signal rather than guessing a duration
      if (submitBtn && 'MutationObserver' in window) {
        var mo = new MutationObserver(function () {
          if (!submitBtn.disabled) { clear(); mo.disconnect(); }
        });
        mo.observe(submitBtn, { attributes: true, attributeFilter: ['disabled'] });
        setTimeout(function () { clear(); mo.disconnect(); }, 6000); // safety net
      } else {
        setTimeout(clear, 1200);
      }
    });
  }

  // fly the slide the buyer is actually looking at, not slide one
  bindAddFeedback(form, 'Added to cart', function () {
    var slides = root.querySelectorAll('.sc-gallery__slide');
    if (!slides.length) return null;
    var i = rail && rail.clientWidth ? Math.round(rail.scrollLeft / rail.clientWidth) : 0;
    var slide = slides[Math.min(Math.max(i, 0), slides.length - 1)];
    return slide ? slide.querySelector('img') : null;
  });

  bindAddFeedback(bundleForm, 'Added to cart', function () {
    var checked = bundleForm && bundleForm.querySelector('[data-bundle-check]:checked');
    var item = checked && checked.closest('[data-bundle-item]');
    return item ? item.querySelector('img') : null;
  });

  /* ----------------------------------------------------------
     Scroll reveal
     Sections fade up once. The hero is deliberately excluded —
     it must paint immediately for LCP.
  ---------------------------------------------------------- */
  (function reveal() {
    var hero = root.querySelector('[data-hero]');
    var targets = root.querySelectorAll('[data-reveal], [data-stagger]');

    // Never leave content invisible if we cannot observe it.
    function showAll() {
      if (hero) hero.classList.add('is-in');
      for (var i = 0; i < targets.length; i++) targets[i].classList.add('is-in');
    }

    if (REDUCED || !('IntersectionObserver' in window)) { showAll(); return; }

    // Hero runs on load, not on scroll — it is already in view. Two rAFs
    // guarantee the start state is painted before the transition begins,
    // otherwise the browser collapses both frames and nothing animates.
    if (hero) {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { hero.classList.add('is-in'); });
      });
    }

    if (targets.length) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

      for (var k = 0; k < targets.length; k++) io.observe(targets[k]);
    }

    // Safety net: if anything is still hidden after 3s, reveal it.
    setTimeout(showAll, 3000);
  })();

  /* ----------------------------------------------------------
     Gallery images resolve instead of popping.
     The first slide is skipped — it carries the LCP element.
  ---------------------------------------------------------- */
  (function imageFade() {
    var imgs = root.querySelectorAll('.sc-gallery__slide:not(:first-child) img');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        if (img.complete && img.naturalWidth) { img.classList.add('is-loaded'); return; }
        img.addEventListener('load', function () { img.classList.add('is-loaded'); });
        // a broken image must not stay invisible
        img.addEventListener('error', function () { img.classList.add('is-loaded'); });
      })(imgs[i]);
    }
  })();

  /* ----------------------------------------------------------
     Boot: honour ?variant= on load
  ---------------------------------------------------------- */
  (function boot() {
    var params = new URLSearchParams(window.location.search);
    var wanted = params.get('variant');
    var matched = false;

    if (wanted) {
      var match = root.querySelector('.scentimental-variant-btn[data-variant-id="' + wanted + '"]');
      if (match) { selectVariant(match); matched = true; }
    }

    if (!matched) {
      // paint the unit price and goal for the server-rendered variant
      var active = root.querySelector('.scentimental-variant-btn.is-active');
      renderUnitPrice(unitCents, active ? active.getAttribute('data-variant-title') : '');
    }

    renderSubtotal();
  })();
})();
